import React from 'react';

import { FormControlLabel, Switch as MuiSwitch, SwitchProps as MuiSwitchProps } from '@mui/material';

import styles from './Switch.module.scss';

export interface SwitchProps extends Omit<MuiSwitchProps, 'onChange'> {
    /**
     * The label for the switch
     */
    label?: string;
    /**
     * If true, the switch is checked (on)
     */
    checked?: boolean;
    /**
     * The value of the switch
     */
    value?: string | number;
    /**
     * The name used to reference the switch
     */
    name?: string;
    /**
     * Callback fired when the switch is toggled
     */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    /**
     * If true, the switch will be disabled
     */
    disabled?: boolean;
    /**
     * Custom class name for the container
     */
    className?: string;
    /**
     * Position of the label relative to the switch
     */
    labelPlacement?: 'start' | 'end' | 'top' | 'bottom';
}

/**
 * Custom Switch component based on MUI Switch
 * Implements design system styling from Figma atoms
 * Supports light and dark themes via CSS variables
 */
export const Switch: React.FC<SwitchProps> = ({
    label,
    checked,
    value,
    name,
    onChange,
    disabled = false,
    className = '',
    labelPlacement = 'end',
    ...props
}) => {
    const containerClass = [styles.container, disabled ? styles.disabled : '', className].filter(Boolean).join(' ');

    const switchComponent = (
        <MuiSwitch
            className={styles.switch}
            checked={checked}
            value={value}
            name={name}
            onChange={onChange}
            disabled={disabled}
            {...props}
        />
    );

    // If there's a label, wrap in FormControlLabel
    if (label) {
        return (
            <FormControlLabel
                control={switchComponent}
                label={label}
                className={containerClass}
                disabled={disabled}
                labelPlacement={labelPlacement}
                classes={{
                    label: styles.label,
                }}
            />
        );
    }

    // Otherwise, just return the switch
    return <div className={containerClass}>{switchComponent}</div>;
};

export default Switch;
